import { useCallback } from 'react';

import type {
  ExternalSigningDeviceType,
  WalletsV2Dataset,
} from '@infinex/types';
import { getWalletsV2NestedRepresentation } from '@infinex/types';
import type { IconComponent } from '@infinex/ui-icons';
import { HotWalletIcon } from '@infinex/ui-icons';
import { useDataset } from '@infinex/user';

import type { AvatarIconOrEmojiValue } from '../../../components/settings';
import {
  buildAvatarFromWallet,
  getWalletAvatarColorWithCustody,
  getWalletAvatarIconWithCustody,
} from '../../../components/settings/manage-wallets/utils/buildAvatarFromWallet';
import type { CustodyIconType } from '../../../utils';
import { getCustodyIcon, getExternalSignerIconType } from '../../../utils';

export type WalletDisplayId = string | 'vault';

export type WalletDisplayInfo = {
  avatar: AvatarIconOrEmojiValue;
  icon: IconComponent;
  label: string;
  isImported: boolean;
};

const FALLBACK_INFO: WalletDisplayInfo = {
  avatar: buildAvatarFromCustodyType('imported'),
  icon: HotWalletIcon,
  isImported: true,
  label: 'Wallet',
};

const BASE_WALLET_INFO_BY_WALLET_ID = new Map<string, WalletDisplayInfo>([
  [
    'vault',
    {
      icon: getCustodyIcon('vault'),
      avatar: buildAvatarFromCustodyType('vault'),
      label: 'Vaults',
      isImported: false,
    },
  ],
]);

const walletInfoByWalletIdCache = new WeakMap<
  WalletsV2Dataset,
  ReadonlyMap<string, WalletDisplayInfo>
>();

function buildWalletInfoByWalletId(walletsV2Data: WalletsV2Dataset) {
  const byId = new Map(BASE_WALLET_INFO_BY_WALLET_ID);
  const { seedPhrases, privateKeys, externalSigningDevices, multiSigWallets } =
    getWalletsV2NestedRepresentation(walletsV2Data);

  for (const phrase of Object.values(seedPhrases)) {
    for (const wallet of Object.values(phrase.wallets)) {
      const custodyType = wallet.isImported ? 'imported' : 'infinex';
      byId.set(wallet.id, {
        icon: getCustodyIcon(custodyType),
        isImported: wallet.isImported,
        label: wallet.label,
        avatar:
          buildAvatarFromWallet(wallet) ||
          buildAvatarFromCustodyType(custodyType),
      });
    }
  }

  for (const pk of Object.values(privateKeys)) {
    byId.set(pk.wallet.id, {
      icon: getCustodyIcon('imported'),
      isImported: pk.wallet.isImported,
      label: pk.wallet.label,
      avatar:
        buildAvatarFromWallet(pk.wallet) ||
        buildAvatarFromCustodyType('imported'),
    });
  }

  for (const device of Object.values(externalSigningDevices)) {
    const custodyType = getExternalSignerIconType(
      device.deviceType as ExternalSigningDeviceType
    );

    for (const wallet of Object.values(device.wallets)) {
      byId.set(wallet.id, {
        icon: getCustodyIcon(custodyType),
        isImported: wallet.isImported,
        label: wallet.label,
        avatar:
          buildAvatarFromWallet(wallet) ||
          buildAvatarFromCustodyType(custodyType),
      });
    }
  }

  for (const multiSigWallet of Object.values(multiSigWallets)) {
    byId.set(multiSigWallet.id, {
      icon: getCustodyIcon('multi-sig'),
      isImported: multiSigWallet.isImported,
      label: multiSigWallet.label,
      avatar:
        buildAvatarFromWallet(multiSigWallet) ||
        buildAvatarFromCustodyType('multi-sig'),
    });
  }

  return byId;
}

/**
 * The pure derivation behind {@link useWalletDisplayInfo}: the per-wallet
 * display info (label + the user's chosen avatar, or the custody default)
 * from a walletsV2 dataset snapshot. Exported for apps that read walletsV2
 * through a different dataset binding (e.g. the confidential app, whose
 * `@infinex/user` instance is provider-less) — same map, same cache.
 */
export function getCachedWalletInfoByWalletId(
  walletsV2Data: WalletsV2Dataset | undefined
): ReadonlyMap<string, WalletDisplayInfo> {
  if (!walletsV2Data) {
    return BASE_WALLET_INFO_BY_WALLET_ID;
  }

  const cachedWalletInfoByWalletId =
    walletInfoByWalletIdCache.get(walletsV2Data);
  if (cachedWalletInfoByWalletId) {
    return cachedWalletInfoByWalletId;
  }

  const builtWalletInfo = buildWalletInfoByWalletId(walletsV2Data);
  walletInfoByWalletIdCache.set(walletsV2Data, builtWalletInfo);
  return builtWalletInfo;
}

export function useWalletDisplayInfo() {
  const walletsV2 = useDataset('walletsV2');
  const walletInfoByWalletId = getCachedWalletInfoByWalletId(walletsV2.data);

  const getWalletDisplayInfo = useCallback(
    (walletId: WalletDisplayId) => {
      return walletInfoByWalletId.get(walletId) || FALLBACK_INFO;
    },
    [walletInfoByWalletId]
  );

  return { getWalletDisplayInfo };
}

function buildAvatarFromCustodyType(
  custodyType: CustodyIconType
): AvatarIconOrEmojiValue {
  return {
    type: 'icon',
    icon: getWalletAvatarIconWithCustody(custodyType),
    color: getWalletAvatarColorWithCustody(custodyType),
  };
}
